import { PageId } from 'constants/pages'
import { getHasBasicSubscription } from 'helpers/getHasBasicSubscription'
import type { TStep } from 'src/types'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'

interface IGetIntellioResumeTargetParams {
  pages: TStep[]
  pageIds: {
    certificate: PageId
    nameEntry: PageId
    offer: PageId
    registration: PageId
    learnWorldsRedirect?: PageId
    report: PageId
  }
}

const getPageIdFromUrl = (): string =>
  window.location.pathname.split('/').pop() || ''

const getHasName = (nameEntryPageId: PageId): boolean => {
  const sessionName = useZustandSessionStore.getState().userName
  const quizName =
    useZustandGlobalStore.getState().lead?.quizzes?.[nameEntryPageId]

  return !!(sessionName || quizName)
}

const getHasCertificate = (certificatePageId: PageId): boolean => {
  const sessionIssuedAt = useZustandSessionStore.getState().certificateIssuedAt
  const quizCertificate =
    useZustandGlobalStore.getState().lead?.quizzes?.[certificatePageId]

  return !!(sessionIssuedAt || quizCertificate)
}

const getHasQuizAnswer = (pageId: PageId): boolean => {
  const sessionAnswers = useZustandSessionStore.getState()
    .userAnswers as Record<string, unknown> | null
  const quizAnswer = (
    useZustandGlobalStore.getState().lead?.quizzes as
      | Record<string, unknown>
      | undefined
  )?.[pageId]

  return !!(sessionAnswers?.[pageId] || quizAnswer)
}

export const getIntellioResumeTarget = ({
  pages,
  pageIds,
}: IGetIntellioResumeTargetParams): PageId => {
  const { lead } = useZustandGlobalStore.getState()
  const { purchaseId, purchasePageId } = useZustandGlobalLocalStore.getState()

  if (purchaseId) {
    return purchasePageId || pages[0].pageId
  }

  const hasEmail = !!lead?.email
  const hasSubscription = getHasBasicSubscription(lead?.subscriptions || null)
  const hasAccount = !!lead?.firebaseToken

  if (hasSubscription && !hasAccount) {
    return pageIds.registration
  }

  if (hasSubscription && hasAccount) {
    if (!getHasName(pageIds.nameEntry)) return pageIds.nameEntry
    if (!getHasCertificate(pageIds.certificate)) return pageIds.certificate
    if (
      pageIds.learnWorldsRedirect &&
      !getHasQuizAnswer(pageIds.learnWorldsRedirect)
    ) {
      return pageIds.learnWorldsRedirect
    }

    return pageIds.report
  }

  if (hasEmail && !hasSubscription) {
    return pageIds.offer
  }

  const pageIdFromUrl = getPageIdFromUrl()
  const pageFromUrl = pages.find((page) => page.pageId === pageIdFromUrl)

  if (!hasEmail && !hasSubscription && !hasAccount && pageFromUrl) {
    return pageFromUrl.pageId
  }

  return pages[0].pageId
}
